<template>
  <main class="grow rn-page">
    <section class="rn-hero">
      <div class="mx-auto max-w-4xl px-4 sm:px-6">
        <nav class="rn-breadcrumb" aria-label="Breadcrumb">
          <NuxtLink :to="localePath('/changelog')">Release notes</NuxtLink>
          <span aria-hidden="true">/</span>
          <time :datetime="release.date">{{ formatReleaseDate(release.date) }}</time>
        </nav>
        <p class="rn-areas">
          <span v-for="area in release.areas" :key="area">{{ releaseAreas[area] }}</span>
        </p>
        <h1>{{ release.title }}</h1>
        <p class="rn-lede">{{ release.summary }}</p>
      </div>
    </section>

    <section class="rn-media">
      <div class="mx-auto max-w-5xl px-4 sm:px-6">
        <div v-if="hasVideo(release)" class="rn-player">
          <iframe
            :src="`https://www.youtube-nocookie.com/embed/${release.video.youtubeId}?rel=0`"
            :title="release.video.title"
            width="1280"
            height="720"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
          ></iframe>
        </div>
        <img
          v-else
          class="rn-hero-image"
          :src="release.hero.src"
          :srcset="heroSrcset(release.hero)"
          :sizes="HERO_SIZES"
          :alt="release.hero.alt"
          :width="release.hero.width"
          :height="release.hero.height"
          fetchpriority="high"
          decoding="async"
        />
      </div>
    </section>

    <div class="mx-auto max-w-4xl px-4 sm:px-6">
      <nav v-if="release.features.length > 1" class="rn-toc" aria-label="In this release">
        <p>In this release</p>
        <ol>
          <li v-for="feature in release.features" :key="feature.id">
            <a :href="`#${feature.id}`">{{ feature.title }}</a>
          </li>
        </ol>
      </nav>

      <section v-for="feature in release.features" :id="feature.id" :key="feature.id" class="rn-feature">
        <h2>{{ feature.title }}</h2>
        <p v-for="paragraph in feature.body" :key="paragraph" class="rn-copy">{{ paragraph }}</p>
        <ul v-if="feature.highlights.length" class="rn-highlights">
          <li v-for="item in feature.highlights" :key="item">{{ item }}</li>
        </ul>

        <ReleaseNoteDeviceStage v-if="feature.motion" :motion="feature.motion" />

        <figure v-else-if="feature.image" class="rn-shot" :class="{ 'rn-shot--has-phone': feature.imagePhone }">
          <picture>
            <source
              v-if="feature.imagePhone"
              media="(max-width: 719px)"
              :srcset="feature.imagePhone.src"
              :width="feature.imagePhone.width"
              :height="feature.imagePhone.height"
            />
            <img
              :src="feature.image.src"
              :alt="feature.image.alt"
              :width="feature.image.width"
              :height="feature.image.height"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </figure>

        <dl class="rn-facts">
          <div class="rn-fact">
            <dt>Where to find it</dt>
            <dd class="rn-fact-where">
              <ol class="rn-path">
                <li v-for="step in pathSteps(feature.whereToFind)" :key="step">{{ step }}</li>
              </ol>
              <a
                v-if="feature.appRoute"
                class="rn-open"
                :href="addReferralToUrl(`https://app.workspace369.com${feature.appRoute}`)"
                data-conversion-event="open_app_click"
                :data-conversion-label="`Release notes: ${feature.title}`"
                data-conversion-location="release-notes"
              >Open in the app<span aria-hidden="true">&nbsp;→</span></a>
            </dd>
          </div>
          <div class="rn-fact">
            <dt>Plans</dt>
            <dd>{{ planLabel(feature.plans) }}</dd>
          </div>
          <div v-if="feature.links.length" class="rn-fact">
            <dt>Learn more</dt>
            <dd class="rn-fact-links">
              <template v-for="link in feature.links" :key="link.href">
                <a v-if="isExternal(link.href)" :href="link.href">{{ link.label }}</a>
                <NuxtLink v-else :to="localePath(link.href)">{{ link.label }}</NuxtLink>
              </template>
            </dd>
          </div>
        </dl>
      </section>

      <section v-if="release.answers?.length" class="rn-answers" aria-labelledby="rn-answers-title">
        <h2 id="rn-answers-title">Quick answers</h2>
        <dl>
          <template v-for="item in release.answers" :key="item.q">
            <dt>{{ item.q }}</dt>
            <dd>{{ item.a }}</dd>
          </template>
        </dl>
      </section>

      <section v-if="related.length" class="rn-related" aria-labelledby="rn-related-title">
        <h2 id="rn-related-title">Related updates</h2>
        <ul>
          <li v-for="item in related" :key="item.slug">
            <NuxtLink :to="localePath(releasePath(item))" class="rn-related-card">
              <img
                :src="item.hero.src.replace(/hero\.webp$/, 'hero-800.webp')"
                :alt="item.hero.alt"
                width="800"
                height="450"
                loading="lazy"
                decoding="async"
              />
              <time :datetime="item.date">{{ formatReleaseDate(item.date) }}</time>
              <strong>{{ item.title }}</strong>
            </NuxtLink>
          </li>
        </ul>
      </section>

      <nav class="rn-pager" aria-label="More release notes">
        <NuxtLink v-if="newer" :to="localePath(releasePath(newer))" class="rn-pager-link">
          <span>Newer</span>
          <strong>{{ newer.title }}</strong>
        </NuxtLink>
        <span v-else></span>
        <NuxtLink v-if="older" :to="localePath(releasePath(older))" class="rn-pager-link rn-pager-link--older">
          <span>Older</span>
          <strong>{{ older.title }}</strong>
        </NuxtLink>
      </nav>
      <p class="rn-all">
        <NuxtLink :to="localePath('/changelog')">All release notes</NuxtLink>
        <span aria-hidden="true">·</span>
        <a href="/changelog/feed.xml" type="application/atom+xml">Subscribe to the feed</a>
      </p>
    </div>

    <Cta03
      plain
      :title="cta.title"
      :body="cta.body"
      :signup-url="cta.url"
      :conversion-label="`Release notes: ${release.slug}`"
    />
  </main>
</template>

<script setup>
import { localizedUrl } from '~/data/locales';
import pricing from '~/data/pricing-features.json';
import {
  formatReleaseDate,
  hasVideo,
  heroSrcset,
  planLabel,
  releaseAreas,
  releaseMetaTitle,
  releasePath,
  releases,
} from '~/data/release-notes';

const props = defineProps({
  release: { type: Object, required: true },
});

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const { addReferralToUrl } = useReferral();
const siteUrl = 'https://workspace369.com';
const canonicalUrl = computed(() =>
  localizedUrl(siteUrl, releasePath(props.release), canonicalLocaleCode.value),
);

const index = computed(() => releases.findIndex((item) => item.slug === props.release.slug));
const newer = computed(() => (index.value > 0 ? releases[index.value - 1] : null));
const older = computed(() => releases[index.value + 1] || null);

// Up to three other releases that share an area, newest first.
const related = computed(() =>
  releases
    .filter((item) => item.slug !== props.release.slug
      && item.areas.some((area) => props.release.areas.includes(area)))
    .slice(0, 3),
);

// The closing band sells this release: the trial opens on the lowest plan that has every
// plan-limited feature in it (plain signup when every feature is on every plan), and the click is tagged with the
// release so the changelog's sign-ups can be counted per note.
const cta = computed(() => {
  const release = props.release;
  const rank = (name) => pricing.plans.findIndex((item) => item.name === name);
  const named = release.features.filter((feature) => feature.plans !== 'all').map((feature) => feature.plans[0]);
  const plan = named.length ? pricing.plans[Math.max(...named.map(rank))] : null;
  const where = pathSteps(release.features[0].whereToFind)[0];
  return {
    title: `${release.title}, in your own workspace`,
    body: `Start your free trial${plan ? ` on ${plan.name}` : ''}, then find it under ${where}.`,
    url: plan ? `https://app.workspace369.com/signup?plan=${plan.slug}` : 'https://app.workspace369.com/signup',
  };
});

const isExternal = (href) => /^https?:\/\//.test(href);
// "Settings → Invoices → Flash Pay" reads as breadcrumb steps.
const pathSteps = (where) => where.split(/\s*→\s*/).filter(Boolean);

const metaTitle = computed(() => releaseMetaTitle(props.release));
const hubUrl = computed(() => localizedUrl(siteUrl, '/changelog', canonicalLocaleCode.value));
// Hero slot: max-w-5xl minus its side padding.
const HERO_SIZES = '(min-width: 1072px) 976px, (min-width: 640px) calc(100vw - 3rem), calc(100vw - 2rem)';
const heroUrl = computed(() => `${siteUrl}${props.release.hero.src}`);

const schema = computed(() => {
  const release = props.release;
  const graph = [
    {
      '@type': 'BlogPosting',
      '@id': `${canonicalUrl.value}#article`,
      headline: release.title,
      description: release.summary,
      inLanguage: canonicalLocaleCode.value,
      datePublished: `${release.date}T12:00:00Z`,
      dateModified: `${release.updated || release.date}T12:00:00Z`,
      image: [heroUrl.value],
      articleSection: 'Release notes',
      keywords: release.areas.map((area) => releaseAreas[area]).join(', '),
      author: { '@id': `${siteUrl}/#organization` },
      publisher: { '@id': `${siteUrl}/#organization` },
      about: { '@id': `${siteUrl}/#software` },
      mainEntityOfPage: { '@id': `${canonicalUrl.value}#webpage` },
      isPartOf: { '@id': `${hubUrl.value}#collection` },
      url: canonicalUrl.value,
    },
  ];
  if (hasVideo(release)) {
    graph.push({
      '@type': 'VideoObject',
      '@id': `${canonicalUrl.value}#video`,
      name: release.video.title,
      description: release.summary,
      thumbnailUrl: [
        `https://i.ytimg.com/vi/${release.video.youtubeId}/maxresdefault.jpg`,
        heroUrl.value,
      ],
      uploadDate: release.video.uploadDate,
      ...(release.video.duration ? { duration: release.video.duration } : {}),
      embedUrl: `https://www.youtube.com/embed/${release.video.youtubeId}`,
      publisher: { '@id': `${siteUrl}/#organization` },
      isPartOf: { '@id': `${canonicalUrl.value}#webpage` },
    });
  }
  for (const feature of release.features.filter((item) => item.motion)) {
    const large = feature.motion.large;
    graph.push({
      '@type': 'VideoObject',
      '@id': `${canonicalUrl.value}#${feature.id}-motion`,
      name: feature.title,
      description: feature.motion.alt,
      thumbnailUrl: [`${siteUrl}${large.light.poster}`],
      contentUrl: `${siteUrl}${large.light.mp4}`,
      uploadDate: `${release.updated || release.date}T12:00:00Z`,
      duration: feature.motion.duration,
      width: large.width,
      height: large.height,
      publisher: { '@id': `${siteUrl}/#organization` },
      isPartOf: { '@id': `${canonicalUrl.value}#webpage` },
    });
  }
  if (release.answers?.length) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${canonicalUrl.value}#answers`,
      inLanguage: canonicalLocaleCode.value,
      isPartOf: { '@id': `${canonicalUrl.value}#webpage` },
      mainEntity: release.answers.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
});

useHead(() => ({
  title: metaTitle.value,
  meta: [
    { name: 'description', content: props.release.summary },
    { name: 'twitter:title', content: metaTitle.value },
    { name: 'twitter:description', content: props.release.summary },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:title', content: metaTitle.value },
    { property: 'og:description', content: props.release.summary },
    { property: 'og:type', content: 'article' },
    { property: 'article:published_time', content: `${props.release.date}T12:00:00Z` },
    { property: 'article:modified_time', content: `${props.release.updated || props.release.date}T12:00:00Z` },
  ],
  link: [
    { key: 'canonical', rel: 'canonical', href: canonicalUrl.value },
    {
      key: 'release-notes-feed',
      rel: 'alternate',
      type: 'application/atom+xml',
      title: 'Workspace369 release notes',
      href: `${siteUrl}/changelog/feed.xml`,
    },
    {
      key: 'release-notes-markdown',
      rel: 'alternate',
      type: 'text/markdown',
      title: 'Workspace369 release notes (Markdown)',
      href: `${siteUrl}/changelog.md`,
    },
    ...(hasVideo(props.release)
      ? []
      : [{
          key: 'release-hero',
          rel: 'preload',
          as: 'image',
          href: props.release.hero.src,
          imagesrcset: heroSrcset(props.release.hero),
          imagesizes: HERO_SIZES,
          fetchpriority: 'high',
        }]),
  ],
  script: [
    { key: 'release-note-schema', type: 'application/ld+json', children: JSON.stringify(schema.value) },
  ],
}));
</script>

<style scoped>
.rn-page { background: #ffffff; }
.dark .rn-page { background: #020617; }

.rn-hero {
  background:
    radial-gradient(circle at 50% 0%, rgb(27 167 206 / 0.12), transparent 30rem),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  padding: 8.5rem 0 2rem;
}
.dark .rn-hero {
  background:
    radial-gradient(circle at 12% 12%, rgb(27 167 206 / 0.18), transparent 28rem),
    linear-gradient(180deg, #020617 0%, #020617 100%);
}
.rn-breadcrumb { display: flex; flex-wrap: wrap; gap: 0.5rem; color: #64748b; font-size: 0.8rem; font-weight: 700; }
.rn-breadcrumb a { color: #0e7490; text-decoration: none; }
.rn-breadcrumb a:hover { text-decoration: underline; }
.dark .rn-breadcrumb { color: #94a3b8; }
.dark .rn-breadcrumb a { color: #67e8f9; }

.rn-areas { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-top: 1.5rem; }
.rn-areas span {
  border: 1px solid #cdeaf3;
  border-radius: 0.25rem;
  background: #f0f9fc;
  padding: 0.15rem 0.45rem;
  color: #0e7490;
  font-size: 0.7rem;
  font-weight: 800;
}
.dark .rn-areas span { border-color: rgb(103 232 249 / 0.22); background: rgb(103 232 249 / 0.06); color: #67e8f9; }

.rn-hero h1 {
  margin-top: 0.9rem;
  color: #020617;
  font-size: clamp(2rem, 4.6vw, 3.25rem);
  font-weight: 900;
  line-height: 1.08;
  letter-spacing: -0.01em;
  text-wrap: balance;
}
.dark .rn-hero h1 { color: #ffffff; }
.rn-lede { margin-top: 1.1rem; max-width: 42rem; color: #475569; font-size: 1.1rem; line-height: 1.7; }
.dark .rn-lede { color: #cbd5e1; }

.rn-media { padding: 0.5rem 0 1rem; }
.rn-hero-image,
.rn-player {
  display: block;
  width: 100%;
  height: auto;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 0.625rem;
  background: #f1f5f9;
  box-shadow: 0 24px 60px rgb(15 23 42 / 0.10);
}
.rn-player { aspect-ratio: 16 / 9; }
.rn-player iframe { display: block; width: 100%; height: 100%; border: 0; }
.dark .rn-hero-image,
.dark .rn-player { border-color: #243247; background: #0b1424; box-shadow: 0 24px 60px rgb(0 0 0 / 0.4); }

.rn-toc {
  margin-top: 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem 1.15rem;
}
.dark .rn-toc { border-color: #243247; }
.rn-toc p { color: #64748b; font-size: 0.72rem; font-weight: 900; text-transform: uppercase; }
.dark .rn-toc p { color: #94a3b8; }
.rn-toc ol { display: grid; gap: 0.4rem; margin-top: 0.6rem; counter-reset: rn-toc; }
.rn-toc li { counter-increment: rn-toc; display: flex; gap: 0.6rem; font-size: 0.92rem; font-weight: 700; }
.rn-toc li::before { content: counter(rn-toc, decimal-leading-zero); color: #94a3b8; font-variant-numeric: tabular-nums; }
.rn-toc a { color: #0f172a; text-decoration: none; }
.rn-toc a:hover { color: #0e7490; text-decoration: underline; }
.dark .rn-toc a { color: #e2e8f0; }
.dark .rn-toc a:hover { color: #67e8f9; }

.rn-feature { scroll-margin-top: 6rem; padding-top: 3rem; }
.rn-feature + .rn-feature { margin-top: 1rem; border-top: 1px solid #eef2f7; }
.dark .rn-feature + .rn-feature { border-color: #1b283c; }
.rn-feature h2 {
  color: #0f172a;
  font-size: clamp(1.45rem, 2.8vw, 1.9rem);
  font-weight: 900;
  line-height: 1.18;
  text-wrap: balance;
}
.dark .rn-feature h2 { color: #f8fafc; }
.rn-copy { margin-top: 0.9rem; max-width: 44rem; color: #334155; font-size: 1.02rem; line-height: 1.75; }
.dark .rn-copy { color: #cbd5e1; }
.rn-highlights { display: grid; gap: 0.5rem; margin-top: 1.1rem; max-width: 44rem; }
.rn-highlights li { position: relative; padding-left: 1.6rem; color: #334155; font-size: 0.98rem; line-height: 1.6; }
.rn-highlights li::before {
  position: absolute;
  top: 0.35rem;
  left: 0;
  width: 1rem;
  height: 1rem;
  border-radius: 0.25rem;
  background: #e0f4fa url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%230e7490' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m4 8.5 2.5 2.5L12 5.5'/%3E%3C/svg%3E") center / 0.75rem no-repeat;
  content: '';
}
.dark .rn-highlights li { color: #cbd5e1; }
.dark .rn-highlights li::before { background-color: rgb(103 232 249 / 0.12); }

.rn-shot { margin: 1.75rem 0 0; }
.rn-shot img {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.625rem;
  box-shadow: 0 18px 48px rgb(15 23 42 / 0.08);
}
.dark .rn-shot img { border-color: #243247; box-shadow: 0 18px 48px rgb(0 0 0 / 0.35); }
@media (max-width: 719px) {
  .rn-shot--has-phone img { max-width: 22rem; margin: 0 auto; }
}

.rn-facts {
  margin-top: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #f8fafc;
  padding: 0 1.15rem;
}
.dark .rn-facts { border-color: #243247; background: #07101f; }
.rn-fact { display: grid; gap: 0.3rem; padding: 0.85rem 0; }
.rn-fact + .rn-fact { border-top: 1px solid #e2e8f0; }
.dark .rn-fact + .rn-fact { border-top-color: #1e293b; }
@media (min-width: 720px) {
  .rn-fact { grid-template-columns: 9rem minmax(0, 1fr); gap: 1.25rem; align-items: baseline; }
}
.rn-facts dt { color: #64748b; font-size: 0.8rem; font-weight: 600; }
.dark .rn-facts dt { color: #94a3b8; }
.rn-facts dd { color: #1e293b; font-size: 0.93rem; font-weight: 500; line-height: 1.5; }
.dark .rn-facts dd { color: #e2e8f0; }
.rn-fact-where { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 0.6rem 1rem; }
.rn-path { display: flex; flex-wrap: wrap; align-items: center; gap: 0.15rem 0; margin: 0; padding: 0; list-style: none; }
.rn-path li + li::before { content: '›'; margin: 0 0.5rem; color: #94a3b8; font-weight: 400; }
.rn-open {
  flex: none;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background: #fff;
  padding: 0.3rem 0.7rem;
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 140ms ease, background-color 140ms ease;
}
.rn-open:hover { border-color: #0e7490; background: #ecfeff; }
.dark .rn-open { border-color: #334155; background: #0b1424; color: #67e8f9; }
.dark .rn-open:hover { border-color: #67e8f9; background: #0f2233; }
.rn-fact-links { display: flex; flex-wrap: wrap; gap: 0.35rem 1.25rem; }
.rn-fact-links a { color: #0e7490; font-weight: 600; text-decoration: none; }
.rn-fact-links a:hover { text-decoration: underline; text-underline-offset: 3px; }
.dark .rn-fact-links a { color: #67e8f9; }

.rn-answers, .rn-related { margin-top: 4rem; }
.rn-answers h2, .rn-related h2 { color: #0f172a; font-size: 1.35rem; font-weight: 900; }
.dark .rn-answers h2, .dark .rn-related h2 { color: #f8fafc; }
.rn-answers dl { margin-top: 1rem; border-top: 1px solid #e2e8f0; }
.dark .rn-answers dl { border-color: #1e293b; }
.rn-answers dt { padding-top: 0.9rem; color: #0f172a; font-weight: 700; }
.rn-answers dd { padding: 0.3rem 0 0.9rem; border-bottom: 1px solid #e2e8f0; color: #475569; }
.dark .rn-answers dt { color: #e2e8f0; }
.dark .rn-answers dd { border-color: #1e293b; color: #cbd5e1; }
.rn-related ul { display: grid; gap: 1rem; margin-top: 1rem; }
@media (min-width: 720px) { .rn-related ul { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.rn-related-card { display: grid; gap: 0.35rem; text-decoration: none; }
.rn-related-card img {
  width: 100%;
  height: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  transition: transform 180ms ease, box-shadow 180ms ease;
}
.rn-related-card:hover img { transform: translateY(-2px); box-shadow: 0 12px 30px rgb(15 23 42 / 0.12); }
.rn-related-card time { margin-top: 0.4rem; color: #64748b; font-size: 0.75rem; font-weight: 800; }
.rn-related-card strong { color: #0f172a; font-size: 0.95rem; line-height: 1.35; }
.dark .rn-related-card img { border-color: #243247; }
.dark .rn-related-card strong { color: #f8fafc; }
@media (prefers-reduced-motion: reduce) {
  .rn-related-card img { transition: none; }
  .rn-related-card:hover img { transform: none; }
}

.rn-pager { display: grid; gap: 0.75rem; margin-top: 4rem; }
@media (min-width: 720px) { .rn-pager { grid-template-columns: 1fr 1fr; } }
.rn-pager-link {
  display: grid;
  gap: 0.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.9rem 1rem;
  text-decoration: none;
  transition: border-color 140ms ease;
}
.rn-pager-link:hover { border-color: rgb(27 167 206 / 0.6); }
.rn-pager-link--older { text-align: right; }
.rn-pager-link span { color: #64748b; font-size: 0.75rem; font-weight: 800; }
.rn-pager-link strong { color: #0f172a; font-size: 0.95rem; line-height: 1.35; }
.dark .rn-pager-link { border-color: #243247; }
.dark .rn-pager-link span { color: #94a3b8; }
.dark .rn-pager-link strong { color: #f8fafc; }
.rn-all { display: flex; flex-wrap: wrap; gap: 0.5rem; margin: 1.5rem 0 4rem; color: #64748b; font-size: 0.9rem; font-weight: 700; }
.rn-all a { color: #0e7490; }
.dark .rn-all { color: #94a3b8; }
.dark .rn-all a { color: #67e8f9; }
</style>
